import styled from "styled-components";
import { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { TERMS_SEO } from '../../seo/pageSeoMeta';
import { purpleColors, violetColors, slateColors, Section, Container } from "./AppLandingPage/styles";

const PageSection = styled(Section)`
  background: linear-gradient(to bottom right, ${slateColors[50]}, white);
  padding-top: 4rem;
  padding-bottom: 5rem;
`;

const PageContainer = styled(Container)`
  max-width: 70rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const HeroCard = styled.div`
  background: linear-gradient(to right, ${purpleColors[50]}, ${violetColors[500]}12);
  border: 1px solid ${purpleColors[100]};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${slateColors[200]};
  border-radius: 1.25rem;
  padding: 1.75rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: 768px) {
    padding: 2.25rem;
    gap: 1.5rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 2.25rem;
  font-weight: 800;
  color: ${slateColors[900]};
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 2.75rem;
  }
`;

const MetaText = styled.p`
  margin: 0.5rem 0 0;
  color: ${slateColors[600]};
  font-size: 1rem;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const SectionTitle = styled.h2`
  margin: 0.9rem 0 0.05rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${slateColors[900]};

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-top: 0.1rem;
`;

const SubTitle = styled.h3`
  margin: 0.65rem 0 0.05rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${purpleColors[700]};

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
`;

const Paragraph = styled.p`
  margin: 0.1rem 0 0;
  color: ${slateColors[700]};
  font-size: 1rem;
  line-height: 1.65;

  @media (min-width: 768px) {
    font-size: 1.05rem;
  }
`;

const TermsConditionsPage = () => {
  const { t } = useTranslation();
  
  // Scroll to top on component mount
  useEffect(() => {
      window.scrollTo(0, 0); // Scroll to the top of the page
  }, []);

    return (
      <>
        <Helmet prioritizeSeoTags>
          <title>{TERMS_SEO.title}</title>
          <meta name="description" content={TERMS_SEO.description} />
          <meta name="keywords" content="chuuchuu terms, train app terms and conditions, railway data terms, service agreement, user policy" />
          
          {/* Prerender.io specific meta tags */}
          <meta name="prerender-status-code" content="200" />
          <meta name="prerender-header" content="Cache-Control: max-age=300, must-revalidate" />
          <meta name="fragment" content="!" />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://chuuchuu.com/terms" />
          <meta property="og:title" content={TERMS_SEO.title} />
          <meta property="og:description" content={TERMS_SEO.description} />
          <meta property="og:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://chuuchuu.com/terms" />
          <meta property="twitter:title" content={TERMS_SEO.title} />
          <meta property="twitter:description" content={TERMS_SEO.description} />
          <meta property="twitter:image" content="https://chuuchuu.com/images/previewpage.jpg" />
          
          {/* Additional SEO tags */}
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://chuuchuu.com/terms" />
        </Helmet>
      
        <PageSection>
          <PageContainer>
            <HeroCard>
              <PageTitle>{t('terms.title')}</PageTitle>
              <MetaText>{t('terms.lastUpdated')}</MetaText>
            </HeroCard>

            <Card>
              <SectionBlock>
                <Paragraph>
                  {t('terms.intro')}
                </Paragraph>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section1Title')}</SectionTitle>
                <SubSection>
                  <Paragraph>{t('terms.section1Intro')}</Paragraph>
                </SubSection>
                
                <SubSection>
                  <SubTitle>{t('terms.section1_1Title')}</SubTitle>
                  <Paragraph>{t('terms.section1_1Content')}</Paragraph>
                </SubSection>

                <SubSection>
                  <SubTitle>{t('terms.section1_2Title')}</SubTitle>
                  <Paragraph>{t('terms.section1_2Content')}</Paragraph>
                </SubSection>

                <SubSection>
                  <SubTitle>{t('terms.section1_3Title')}</SubTitle>
                  <Paragraph>{t('terms.section1_3Content')}</Paragraph>
                </SubSection>

                <SubSection>
                  <SubTitle>{t('terms.section1_4Title')}</SubTitle>
                  <Paragraph>{t('terms.section1_4Content')}</Paragraph>
                </SubSection>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section2Title')}</SectionTitle>
                <Paragraph>
                  {t('terms.section2Content')}
                </Paragraph>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section3Title')}</SectionTitle>
                <SubSection>
                  <SubTitle>{t('terms.section3_1Title')}</SubTitle>
                  <Paragraph>{t('terms.section3_1Content')}</Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section3_2Title')}</SubTitle>
                  <Paragraph>{t('terms.section3_2Content')}</Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section3_3Title')}</SubTitle>
                  <Paragraph>{t('terms.section3_3Content')}</Paragraph>
                </SubSection>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section4Title')}</SectionTitle>
                <SubSection>
                  <SubTitle>{t('terms.section4_1Title')}</SubTitle>
                  <Paragraph>{t('terms.section4_1Content')}</Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section4_2Title')}</SubTitle>
                  <Paragraph>{t('terms.section4_2Content')}</Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section4_3Title')}</SubTitle>
                  <Paragraph>{t('terms.section4_3Content')}</Paragraph>
                </SubSection>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section5Title')}</SectionTitle>
                <SubSection>
                  <SubTitle>{t('terms.section5_1Title')}</SubTitle>
                  <Paragraph>{t('terms.section5_1Content')}</Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section5_2Title')}</SubTitle>
                  <Paragraph>{t('terms.section5_2Content')}</Paragraph>
                </SubSection>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section6Title')}</SectionTitle>
                <Paragraph>
                  {t('terms.section6Content')}
                </Paragraph>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section7Title')}</SectionTitle>
                <SubSection>
                  <SubTitle>{t('terms.section7_1Title')}</SubTitle>
                  <Paragraph>
                  {t('terms.section7_1Content')}
                  </Paragraph>
                </SubSection>
                <SubSection>
                  <SubTitle>{t('terms.section7_2Title')}</SubTitle>
                  <Paragraph>
                  {t('terms.section7_2Content')}
                  </Paragraph>
                </SubSection>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section8Title')}</SectionTitle>
                <Paragraph>
                  {t('terms.section8Content')}
                </Paragraph>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>{t('terms.section9Title')}</SectionTitle>
                <Paragraph>
                  {t('terms.section9Content')}
                </Paragraph>
              </SectionBlock>
    
              <SectionBlock>
                <SectionTitle>{t('terms.section10Title')}</SectionTitle>
                <Paragraph>
                {t('terms.section10Content')}
                  <br />
                  <br />
                  chuuchuu
                  <br />
                  {t('terms.contactEmail')}
                </Paragraph>
              </SectionBlock>
            </Card>
          </PageContainer>
        </PageSection>
      </>
    );
  };
  
  export default TermsConditionsPage;